import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Cookie, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { isAndroidWebView, isInAppBrowser } from '@/utils/webviewDetect';
import { updateGoogleAdsConsent } from '@/utils/gadsConversion';

const COOKIE_CONSENT_KEY = 'bodymax_cookie_consent';

export function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const { user, api, loading } = useAuth();
  const serverCheckDone = useRef(false);
  const guestTimerRef = useRef(null);

  // Hide cookie banner inside installed apps (WebToNative, PWA standalone, Android WebView)
  const isInstalledApp = typeof window !== 'undefined' && (
    window.WTN ||
    (isAndroidWebView && !isInAppBrowser) ||
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true ||
    new URLSearchParams(window.location.search).get('utm_source') === 'google_play'
  );

  useEffect(() => {
    // Don't do anything while auth is loading
    if (loading) {
      return;
    }

    // Check localStorage first (fast path)
    const localConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (localConsent) {
      setShowBanner(false);
      // Clear any pending guest timer
      if (guestTimerRef.current) {
        clearTimeout(guestTimerRef.current);
        guestTimerRef.current = null;
      }
      return;
    }

    // For logged-in users, check server-side consent
    if (user && api) {
      // Clear any pending guest timer since user is now logged in
      if (guestTimerRef.current) {
        clearTimeout(guestTimerRef.current);
        guestTimerRef.current = null;
      }
      
      // If we already did a server check for this user session, don't re-do it
      if (serverCheckDone.current) {
        return;
      }
      
      serverCheckDone.current = true;
      api.get('/user/cookie-consent')
        .then(res => {
          if (res.data?.acknowledged) {
            // Sync to localStorage for instant future checks
            localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
            setShowBanner(false);
          } else {
            // Server says no consent — show banner after delay
            setTimeout(() => setShowBanner(true), 1000);
          }
        })
        .catch(() => {
          // API failure — show banner after delay
          setTimeout(() => setShowBanner(true), 1000);
        });
    } else {
      // Guest user with no localStorage consent
      // Only start the timer if we haven't already and user isn't loading
      if (!guestTimerRef.current && !serverCheckDone.current) {
        guestTimerRef.current = setTimeout(() => {
          // Double-check localStorage wasn't set in the meantime
          if (!localStorage.getItem(COOKIE_CONSENT_KEY)) {
            setShowBanner(true);
          }
          guestTimerRef.current = null;
        }, 1500); // Slightly longer delay for guest to allow auth to complete
      }
    }
    
    // Cleanup
    return () => {
      if (guestTimerRef.current) {
        clearTimeout(guestTimerRef.current);
      }
    };
  }, [user, api, loading]);

  // Early return for installed app users — after all hooks
  if (isInstalledApp) return null;

  const handleAccept = async () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
    setShowBanner(false);
    updateGoogleAdsConsent(true);

    // Persist to server for logged-in users
    if (user && api) {
      try {
        await api.post('/user/cookie-consent');
      } catch {
        // Non-critical — localStorage is the fallback
      }
    }
  };

  const handleDecline = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'declined');
    setShowBanner(false);
    updateGoogleAdsConsent(false);
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="fixed bottom-0 left-0 right-0 z-[100] p-4"
          data-testid="cookie-consent-banner"
        >
          <div className="max-w-lg mx-auto bg-card border border-border rounded-2xl shadow-xl backdrop-blur-xl p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              {/* Icon */}
              <div className="hidden sm:flex items-center justify-center w-12 h-12 rounded-full bg-accent-deep flex-shrink-0">
                <Cookie size={24} className="text-accent" />
              </div>

              {/* Text */}
              <div className="flex-1">
                <h3 className="font-heading font-semibold text-foreground mb-1">
                  We value your privacy
                </h3>
                <p className="text-sm text-muted-foreground">
                  We use cookies to enhance your experience, analyze site traffic, and personalize content. 
                  By clicking "Accept", you consent to our use of cookies.{' '}
                  <a 
                    href="/privacy" 
                    className="text-accent hover:underline"
                  >
                    Learn more
                  </a>
                </p>
              </div>

              {/* Buttons */}
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <Button
                  variant="ghost"
                  onClick={handleDecline}
                  className="flex-1 sm:flex-none rounded-full text-muted-foreground hover:text-foreground"
                  data-testid="cookie-decline-btn"
                >
                  Decline
                </Button>
                <Button
                  onClick={handleAccept}
                  className="flex-1 sm:flex-none bg-accent text-accent-foreground hover:bg-primary/90 rounded-full font-semibold px-6"
                  data-testid="cookie-accept-btn"
                >
                  Accept
                </Button>
              </div>

              {/* Close button for mobile */}
              <Button
                variant="ghost"
                size="icon"
                onClick={handleDecline}
                className="absolute top-2 right-2 sm:hidden rounded-full h-8 w-8"
              >
                <X size={16} />
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
